/**
 * Was beim Überfahren geschieht (HM-142, nach `components/data/Chart.jsx` des
 * Entwurfs): Der Abschnitt unter dem Zeiger wird **hinterlegt**, die übrigen
 * Balken treten zurück, und über einem Bild mit Linien steht in der Mitte des
 * Abschnitts eine **gestrichelte senkrechte Linie** — dort, wo die Punkte der
 * Linien sitzen.
 *
 * **Der Abschnitt, nicht der Zeitpunkt.** Über Balken meint der Zeiger den
 * Balken, unter dem er steht (`spanAt`, HM-099) — dieselbe Regel wie im
 * Ablesefenster. Hinterlegung, Fadenkreuz und Fenster nennen damit immer
 * denselben Abschnitt.
 *
 * Hinterlegung und Linie sind DOM-Elemente über der Zeichenfläche und keine
 * Striche im Canvas: Sie folgen dem Zeiger, ohne dass die Reihen neu gezeichnet
 * werden müssen. Neu gezeichnet wird nur, wenn der Abschnitt wechselt — für das
 * Zurücktreten der übrigen Balken.
 */
import type uPlot from "uplot";

export interface HoverState {
  /** Ob jeder Zeitpunkt ein Abschnitt ist (`frame.bucketed`). */
  readonly bucketed: boolean;
  /** Ob im Bild eine Linie steht — dann gibt es die senkrechte Linie in der Mitte. */
  readonly lines: boolean;
  /** Welcher Index zu einer Zeigerposition gehört; `null` außerhalb jedes Abschnitts. */
  readonly locate: (at: number, times: readonly number[]) => number | null;
  /** Das Ende des Abschnitts an `index`, in Millisekunden. */
  readonly spanEnd: (times: readonly number[], index: number) => number;
}

export function hoverPlugin(
  read: () => HoverState,
  onChange: (u: uPlot, index: number | null) => void,
): uPlot.Plugin {
  let band: HTMLDivElement | null = null;
  let line: HTMLDivElement | null = null;
  let current: number | null = null;

  const hide = () => {
    if (band !== null) band.style.display = "none";
    if (line !== null) line.style.display = "none";
  };

  return {
    hooks: {
      init: (u) => {
        band = document.createElement("div");
        band.className = "hm-chart-hover";
        line = document.createElement("div");
        line.className = "hm-chart-hover-line";
        hide();
        u.over.prepend(band);
        u.over.append(line);
      },

      destroy: () => {
        band?.remove();
        line?.remove();
        band = null;
        line = null;
      },

      setCursor: (u) => {
        const state = read();
        const left = u.cursor.left;
        const times = u.data[0] as readonly number[];
        const index =
          left === undefined || left < 0 || times.length === 0
            ? null
            : state.bucketed
              ? state.locate(u.posToVal(left, "x"), times)
              : (u.cursor.idx ?? null);

        if (index === null || !state.bucketed) {
          hide();
        } else {
          const x0 = Math.max(u.valToPos(times[index]!, "x"), 0);
          const x1 = Math.min(u.valToPos(state.spanEnd(times, index), "x"), u.over.clientWidth);
          if (band !== null) {
            band.style.display = "block";
            band.style.left = `${x0}px`;
            band.style.width = `${Math.max(x1 - x0, 1)}px`;
          }
          if (line !== null) {
            line.style.display = state.lines ? "block" : "none";
            line.style.left = `${(x0 + x1) / 2}px`;
          }
        }

        if (index !== current) {
          current = index;
          onChange(u, index);
        }
      },
    },
  };
}
